/**
 * @unscoped — ⑤ 우측 레일 메모의 i18n (design-20260724-rightrail-memo Phase 1).
 *
 * cycle 51 calendar / 70 layout 등 기존 도메인 모듈과 동일 패턴:
 *   - lang state 는 base-i18n 의 globalThis 단일 source, 본 모듈은 MESSAGES dict + tMemo helper
 *   - fallback chain: lang → en → FALLBACK_LANG(ko)
 *   - params 치환 ({n}) 지원
 */
import {
  getSharedLang, subscribeSharedLang, FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== 패널 헤더 / 진입점 =====
  'memo.title': { ko: '메모', en: 'Memo', ja: 'メモ' },
  'memo.open': { ko: '메모 열기', en: 'Open memo', ja: 'メモを開く' },
  'memo.close': { ko: '닫기', en: 'Close', ja: '閉じる' },
  'memo.new': { ko: '새 메모', en: 'New memo', ja: '新規メモ' },
  'memo.back': { ko: '목록', en: 'Back to list', ja: '一覧へ' },
  'memo.dock_inline': { ko: '나란히 보기', en: 'Dock inline', ja: '並べて表示' },
  'memo.dock_overlay': { ko: '겹쳐 보기', en: 'Dock as overlay', ja: '重ねて表示' },
  'memo.resize': { ko: '메모 폭 조절 (드래그 · 더블클릭 초기화)', en: 'Resize memo panel (drag, double-click to reset)', ja: 'メモ幅を調整 (ドラッグ・ダブルクリックで初期化)' },

  // ===== 목록 =====
  'memo.search_placeholder': { ko: '메모 검색…', en: 'Search memos…', ja: 'メモを検索…' },
  'memo.group_pinned': { ko: '고정', en: 'Pinned', ja: '固定' },
  'memo.group_context': { ko: '이 화면 · {name}', en: 'This screen · {name}', ja: 'この画面 · {name}' },
  'memo.group_all': { ko: '전체', en: 'All', ja: 'すべて' },
  'memo.group_search': { ko: '검색 결과 {n}건', en: '{n} results', ja: '検索結果 {n}件' },
  'memo.more_count': { ko: '더 보기 ({n})', en: 'Show more ({n})', ja: 'もっと見る ({n})' },
  'memo.load_more': { ko: '더 불러오기', en: 'Load more', ja: 'もっと読み込む' },
  'memo.context_first_on': { ko: '이 화면 우선 정렬 켬 — 누르면 최근순', en: 'Screen-first sorting on — click for recent order', ja: 'この画面優先の並び — クリックで新着順' },
  'memo.context_first_off': { ko: '최근순 — 누르면 이 화면 우선', en: 'Recent order — click for screen-first', ja: '新着順 — クリックでこの画面優先' },
  'memo.untitled': { ko: '제목 없음', en: 'Untitled', ja: '無題' },
  'memo.pin': { ko: '고정', en: 'Pin', ja: '固定' },
  'memo.unpin': { ko: '고정 해제', en: 'Unpin', ja: '固定解除' },
  'memo.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'memo.confirm_delete': { ko: '이 메모를 삭제하시겠습니까?', en: 'Delete this memo?', ja: 'このメモを削除しますか?' },
  'memo.goto_context': { ko: '{name} 화면으로 이동', en: 'Go to {name}', ja: '{name} 画面へ移動' },

  // ===== 빈 상태 =====
  'memo.empty_title': { ko: '아직 메모가 없습니다', en: 'No memos yet', ja: 'メモがまだありません' },
  'memo.empty_hint': { ko: '어느 화면에서든 적어 두세요', en: 'Jot things down from any screen', ja: 'どの画面からでも書き留められます' },
  'memo.empty_cta': { ko: '첫 메모 쓰기', en: 'Write your first memo', ja: '最初のメモを書く' },
  'memo.empty_search': { ko: '검색 결과가 없습니다', en: 'No results', ja: '検索結果がありません' },

  // ===== 편집 =====
  'memo.title_placeholder': { ko: '제목 (생략 시 첫 줄)', en: 'Title (defaults to first line)', ja: 'タイトル (省略時は先頭行)' },
  'memo.body_placeholder': { ko: '메모를 입력하세요…', en: 'Write a memo…', ja: 'メモを入力…' },
  'memo.char_count': { ko: '{n}자', en: '{n} chars', ja: '{n}文字' },

  // ===== 본문 내 찾기 (2026-08-18) =====
  'memo.find_placeholder': { ko: '이 메모에서 찾기…', en: 'Find in this memo…', ja: 'このメモ内を検索…' },
  'memo.find_count': { ko: '{n}/{total}', en: '{n}/{total}', ja: '{n}/{total}' },
  'memo.find_none': { ko: '없음', en: 'None', ja: 'なし' },
  'memo.find_prev': { ko: '이전 일치 (Shift+Enter)', en: 'Previous match (Shift+Enter)', ja: '前の一致 (Shift+Enter)' },
  'memo.find_next': { ko: '다음 일치 (Enter)', en: 'Next match (Enter)', ja: '次の一致 (Enter)' },
  'memo.find_clear': { ko: '찾기 지우기', en: 'Clear search', ja: '検索をクリア' },

  // ===== 저장 상태 =====
  'memo.saving': { ko: '저장 중…', en: 'Saving…', ja: '保存中…' },
  'memo.saved': { ko: '저장됨 · {time}', en: 'Saved · {time}', ja: '保存済み · {time}' },
  'memo.save_failed': { ko: '저장 실패 — 다시 시도', en: 'Save failed — retry', ja: '保存失敗 — 再試行' },

  // ===== 충돌 =====
  'memo.conflict_title': { ko: '다른 곳에서 수정됨', en: 'Edited elsewhere', ja: '他の場所で編集されました' },
  'memo.conflict_desc': { ko: '이 메모가 다른 탭/기기에서 먼저 저장됐습니다.', en: 'This memo was saved from another tab or device.', ja: '他のタブ・端末で先に保存されました。' },
  'memo.conflict_overwrite': { ko: '내 내용으로 덮어쓰기', en: 'Keep mine', ja: '自分の内容で上書き' },
  'memo.conflict_take_server': { ko: '최신 내용 불러오기', en: 'Load latest', ja: '最新を読み込む' },

  // ===== 상대 시각 (notification-i18n 동형) =====
  'memo.time.now': { ko: '방금', en: 'just now', ja: 'たった今' },
  'memo.time.min': { ko: '{n}분 전', en: '{n}m ago', ja: '{n}分前' },
  'memo.time.hour': { ko: '{n}시간 전', en: '{n}h ago', ja: '{n}時間前' },
  'memo.time.yesterday': { ko: '어제', en: 'yesterday', ja: '昨日' },
  'memo.time.day': { ko: '{n}일 전', en: '{n}d ago', ja: '{n}日前' },

  'memo.load_failed': { ko: '메모를 불러오지 못했습니다', en: 'Failed to load memos', ja: 'メモを読み込めませんでした' },
  'memo.retry': { ko: '다시 시도', en: 'Retry', ja: '再試行' },
};

export type MemoI18nKey = keyof typeof MESSAGES;

export function tMemo(key: MemoI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

export function getMemoLang(): Lang { return getSharedLang(); }
export function subscribeMemoLang(fn: () => void): () => void { return subscribeSharedLang(fn); }
